"use client";

import { Suspense, useCallback, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { authClient } from "@/lib/auth-client";
import { normalizePostAuthRedirect, safeRedirect } from "@/lib/auth-redirect";
import { track } from "@/lib/analytics";
import { useBetaStatus, useClaimBetaInvite } from "@/lib/hooks";
import { readHeldBetaClaimKind } from "@/lib/beta-claim";
import {
  markPendingSignupIntent,
  clearPendingSignupIntent,
} from "@/lib/pending-signup";
import type { BetaClaimKind } from "@/lib/api/beta";
import { AuthShell } from "../components/AuthShell";
import { Button } from "@batch-and-bowl/ui/button";
import { Input } from "@batch-and-bowl/ui/input";
import { Label } from "@batch-and-bowl/ui/label";
import {
  AuthCard,
  AuthCardContent,
  AuthCardDescription,
  AuthCardHeader,
  AuthCardKicker,
  AuthCardTitle,
  AuthCardFooter,
} from "../components/AuthCard";
import { Loader2 } from "lucide-react";

import { BrandMark } from "../components/Brand";

// Better Auth's "already exists" errors name the email as taken outright,
// which would let a sign-up attempt be used to enumerate registered
// accounts. Swap them for copy that doesn't confirm or deny existence.
const ACCOUNT_EXISTS_CODES = new Set([
  "USER_ALREADY_EXISTS",
  "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL",
]);

function SignUpForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Sanitized: an attacker-crafted ?redirect= must never leave this origin.
  const redirectTo = normalizePostAuthRedirect(
    safeRedirect(searchParams.get("redirect")),
  );

  const betaStatus = useBetaStatus();
  const claimInvite = useClaimBetaInvite();
  const inviteRequired = betaStatus.data?.required ?? false;
  const inviteFromUrl = searchParams.get("invite");

  const [inviteCode, setInviteCode] = useState(inviteFromUrl ?? "");
  const [inviteAccepted, setInviteAccepted] = useState(false);
  const [inviteError, setInviteError] = useState("");

  // A visitor can already hold a claim without having typed anything here: a
  // household invitee is sent from /join/[token] after the token was claimed
  // server-side, with a `redirect` param and no `?invite=`. The claim cookie
  // is httpOnly, so this reads the claim route's readable marker instead.
  // Effect, not initial state: the server render has no cookies, and reading
  // during render would hydrate mismatched.
  const [heldClaimKind, setHeldClaimKind] = useState<BetaClaimKind | null>(
    null,
  );
  useEffect(() => {
    setHeldClaimKind(readHeldBetaClaimKind());
  }, []);

  const applyInvite = useCallback(
    async (value: string) => {
      if (!value.trim()) {
        return false;
      }
      setInviteError("");
      try {
        await claimInvite.mutateAsync({ type: "code", value });
        setInviteAccepted(true);
        return true;
      } catch (err) {
        setInviteAccepted(false);
        setInviteError(
          err instanceof Error && err.message
            ? err.message
            : "This invite is invalid or has expired",
        );
        return false;
      }
    },
    [claimInvite],
  );

  // One-click links: /sign-up?invite=BB-XXXX-XXXX claims on arrival so the
  // visitor never types anything.
  useEffect(() => {
    if (inviteRequired && inviteFromUrl) {
      void applyInvite(inviteFromUrl);
    }
    // applyInvite is intentionally omitted: it closes over a fresh mutation
    // object each render, and this must fire once per invite value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteRequired, inviteFromUrl]);

  const googleErrorParam = searchParams.get("googleError");
  const oauthError = searchParams.get("error");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  // Production only (requireEmailVerification: see apps/api/src/auth.ts):
  // Better Auth creates no session on sign-up, so there's nothing to
  // navigate to yet — show an in-page prompt instead of pushing redirectTo
  // and landing the user on a bounce back to /sign-in.
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }

    setError("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    try {
      const result = await authClient.signUp.email({
        name,
        email,
        password,
      });

      if (result.error) {
        if (result.error.code && ACCOUNT_EXISTS_CODES.has(result.error.code)) {
          setError(
            "Unable to create your account with these details. Please try again.",
          );
        } else {
          setError(result.error.message || "Failed to create account");
        }
        return;
      }

      // Account created successfully at this point regardless of whether a
      // session was issued immediately (see the token check below).
      track("signed_up", { method: "email" });

      // No error, but also no session token: requireEmailVerification is on
      // (production) and Better Auth deliberately skipped auto-sign-in (see
      // the installed better-auth 1.6.29's dist/api/routes/sign-up.mjs
      // `shouldSkipAutoSignIn` branch). There's no session to carry the user
      // into redirectTo yet, so show an in-page prompt instead.
      if (!result.data?.token) {
        setAwaitingVerification(true);
        return;
      }

      router.push(redirectTo);
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Sign up error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Arm Google on either signal: a claim just made on this page, or one made
  // before arriving here (issue #308 -- previously only the former counted, so
  // household invitees could never use Google). Both are hints; the server-side
  // gate re-validates on the OAuth callback either way, so a wrong guess here
  // costs a rejected sign-up, never an unearned account.
  const googleBlocked =
    inviteRequired && !inviteAccepted && heldClaimKind === null;

  const handleGoogleSignUp = async () => {
    if (isGoogleLoading || googleBlocked) {
      return;
    }
    setIsGoogleLoading(true);
    try {
      const intent = markPendingSignupIntent();
      const newUserCallbackURL = new URL(redirectTo, window.location.origin);
      newUserCallbackURL.searchParams.set("bb_signup", "new");
      if (intent) newUserCallbackURL.searchParams.set("intent", intent);
      await authClient.signIn.social({
        provider: "google",
        callbackURL: redirectTo,
        ...(intent
          ? {
              newUserCallbackURL: `${newUserCallbackURL.pathname}${newUserCallbackURL.search}${newUserCallbackURL.hash}`,
            }
          : {}),
        // Without this, a gate rejection during the OAuth callback redirects to
        // better-auth's built-in error page, which sanitizes any punctuated
        // message down to "UNKNOWN".
        errorCallbackURL: "/sign-up?googleError=1",
      });
    } catch (err) {
      clearPendingSignupIntent();
      setError("Failed to sign up with Google");
      console.error("Google sign up error:", err);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (isResending) {
      return;
    }
    setIsResending(true);
    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: "/verify-email",
      });
    } catch (err) {
      console.error("Resend verification error:", err);
    } finally {
      // Non-enumerating by design: the endpoint always succeeds the same
      // way, so we show the same copy regardless of outcome.
      setResendSent(true);
      setIsResending(false);
    }
  };

  if (awaitingVerification) {
    return (
      <AuthShell>
        <AuthCard>
          <AuthCardHeader>
            <div className="flex justify-center mb-4">
              <BrandMark size={56} />
            </div>
            <AuthCardKicker>Almost there</AuthCardKicker>
            <AuthCardTitle>Check your email</AuthCardTitle>
          </AuthCardHeader>

          <AuthCardContent className="space-y-4">
            <p
              role="status"
              aria-live="polite"
              className={
                resendSent
                  ? "rounded-md bg-sage-100 p-3 text-sm text-center text-sage-800 dark:bg-sage-900/40 dark:text-sage-200"
                  : "text-sm text-center"
              }
            >
              {resendSent
                ? "Verification email sent. Check your inbox."
                : "We've emailed you a link to verify your account before you can sign in."}
            </p>

            {!resendSent && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleResendVerification}
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Resend verification email"
                )}
              </Button>
            )}
          </AuthCardContent>

          <AuthCardFooter className="flex justify-center">
            <p className="text-sm text-muted-foreground">
              <Link
                href="/sign-in"
                className="text-terracotta-700 dark:text-terracotta-300 hover:underline font-medium"
              >
                Back to sign in
              </Link>
            </p>
          </AuthCardFooter>
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard>
        <AuthCardHeader>
          <div className="flex justify-center mb-4">
            <BrandMark size={56} />
          </div>
          <AuthCardKicker>Getting started</AuthCardKicker>
          <AuthCardTitle>Create an account</AuthCardTitle>
          <AuthCardDescription>
            Start planning delicious meals today
          </AuthCardDescription>
        </AuthCardHeader>

        <AuthCardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="p-3 text-sm text-destructive-text bg-destructive/10 rounded-md"
              >
                {error}
              </div>
            )}

            {(googleErrorParam || oauthError) && (
              <p role="alert" className="text-sm text-destructive-text">
                {oauthError === "Sign_ups_are_invite_only"
                  ? "You need a valid invite code before signing up with Google."
                  : oauthError === "The_beta_is_full"
                    ? "The beta is full right now. Please check back soon."
                    : "We could not complete that Google sign-up. Please try again."}
              </p>
            )}

            {inviteRequired && heldClaimKind === "household" && (
              // The code field below stays available on purpose: a claim lasts
              // 30 minutes, and someone who sits on this page past that needs
              // a way back in without hunting for the join link again.
              <p role="status" className="p-3 text-sm bg-primary/10 rounded-md">
                Your household invite is applied. Create your account and you
                will join the household next — no invite code needed.
              </p>
            )}

            {inviteRequired && (
              <div className="space-y-2">
                <Label htmlFor="invite-code">Invite code</Label>
                <div className="flex gap-2">
                  <Input
                    id="invite-code"
                    value={inviteCode}
                    onChange={(event) => {
                      setInviteCode(event.target.value);
                      setInviteAccepted(false);
                    }}
                    placeholder="BB-XXXX-XXXX"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void applyInvite(inviteCode)}
                    disabled={claimInvite.isPending || inviteAccepted}
                  >
                    {inviteAccepted ? "Applied" : "Apply"}
                  </Button>
                </div>
                {inviteError && (
                  <p role="alert" className="text-sm text-destructive-text">
                    {inviteError}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                At least 8 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create account"
              )}
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            By creating an account you agree to the{" "}
            <Link
              href="/terms"
              className="font-medium text-terracotta-700 dark:text-terracotta-300 hover:underline"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy"
              className="font-medium text-terracotta-700 dark:text-terracotta-300 hover:underline"
            >
              Privacy Policy
            </Link>
            .
          </p>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignUp}
            disabled={isLoading || isGoogleLoading || googleBlocked}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </Button>
        </AuthCardContent>

        <AuthCardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              href="/sign-in"
              className="text-terracotta-700 dark:text-terracotta-300 hover:underline font-medium"
            >
              Sign in
            </Link>
          </p>
        </AuthCardFooter>
      </AuthCard>
    </AuthShell>
  );
}

export default function SignUpPage() {
  // useSearchParams() requires a Suspense boundary during static prerender —
  // without one, `next build` fails on this page.
  return (
    <Suspense
      fallback={
        <AuthShell>
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </AuthShell>
      }
    >
      <SignUpForm />
    </Suspense>
  );
}
